import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabaseServiceRole } from '../../services/supabase';
import { Database } from '../../types/supabase';
import { Button } from '../../components/ui/Button';
import { useNavigate } from 'react-router-dom';

type Category = Database['public']['Tables']['categories']['Row'];
type CategoryInput = Database['public']['Tables']['categories']['Insert'];

const AdminCategoriesList = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [categoryForm, setCategoryForm] = useState<CategoryInput>({
        name: '',
        description: '',
        image_url: ''
    });
    const [error, setError] = useState<string | null>(null);

    // Получение списка категорий, сортировка по полю sort_order
    const { data: categories, isLoading, isError } = useQuery({
        queryKey: ['categories'],
        queryFn: async () => {
            try {
                console.log('Получение списка категорий (прямой доступ через компонент)');

                // Сначала получаем все категории
                const { data, error } = await supabaseServiceRole
                    .from('categories')
                    .select('*')
                    .order('sort_order', { ascending: true });

                if (error) {
                    console.error('Ошибка получения категорий:', error);
                    throw error;
                }

                // Если у категорий нет поля sort_order (равно null), проставляем его на основе текущего порядка
                if (data && data.length > 0 && data[0].sort_order === null) {
                    console.log('Инициализация поля sort_order для категорий');

                    // Проставляем порядок и обновляем каждую категорию
                    const updatePromises = data.map((category, index) => {
                        return supabaseServiceRole
                            .from('categories')
                            .update({ sort_order: index + 1 })
                            .eq('id', category.id);
                    });

                    await Promise.all(updatePromises);

                    // Получаем обновленные данные
                    const { data: updatedData, error: updatedError } = await supabaseServiceRole
                        .from('categories')
                        .select('*')
                        .order('sort_order', { ascending: true });

                    if (updatedError) throw updatedError;
                    return updatedData;
                }

                return data;
            } catch (error) {
                console.error('Ошибка при получении категорий:', error);
                throw error;
            }
        },
    });

    // Мутация для создания новой категории
    const createCategoryMutation = useMutation({
        mutationFn: async (newCategory: CategoryInput) => {
            try {
                console.log('Создание категории (прямой доступ через компонент):', newCategory);

                // Определяем максимальный sort_order для существующих категорий
                const { data: maxSortData } = await supabaseServiceRole
                    .from('categories')
                    .select('sort_order')
                    .order('sort_order', { ascending: false })
                    .limit(1);

                const maxSortOrder = maxSortData && maxSortData.length > 0 && maxSortData[0].sort_order
                    ? maxSortData[0].sort_order
                    : 0;

                // Добавляем к новой категории sort_order на 1 больше максимального
                const { data, error } = await supabaseServiceRole
                    .from('categories')
                    .insert({
                        ...newCategory,
                        sort_order: maxSortOrder + 1
                    })
                    .select();

                if (error) {
                    console.error('Ошибка создания категории:', error);
                    throw error;
                }

                return data;
            } catch (error) {
                console.error('Ошибка при создании категории:', error);
                throw error;
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['categories'] });
            resetForm();
            setIsModalOpen(false);
        },
        onError: (error) => {
            console.error('Ошибка при создании категории:', error);
            setError('Не удалось создать категорию. Попробуйте еще раз.');
        }
    });

    // Мутация для изменения порядка категории (перемещение вверх или вниз)
    const moveOrderMutation = useMutation({
        mutationFn: async ({ categoryId, direction }: { categoryId: string, direction: 'up' | 'down' }) => {
            try {
                if (!categories || categories.length === 0) {
                    throw new Error('Список категорий пуст');
                }

                const categoryToMove = categories.find(cat => cat.id === categoryId);
                if (!categoryToMove) {
                    throw new Error('Категория не найдена');
                }

                // Находим текущий индекс категории в массиве
                const currentIndex = categories.findIndex(cat => cat.id === categoryId);
                if (currentIndex === -1) {
                    throw new Error('Категория не найдена в списке');
                }

                // Определяем новый индекс в зависимости от направления
                let newIndex;
                if (direction === 'up') {
                    // Если категория уже первая, ничего не делаем
                    if (currentIndex === 0) {
                        console.log('Нет категории выше');
                        return null;
                    }
                    newIndex = currentIndex - 1;
                } else {
                    // Если категория уже последняя, ничего не делаем
                    if (currentIndex === categories.length - 1) {
                        console.log('Нет категории ниже');
                        return null;
                    }
                    newIndex = currentIndex + 1;
                }

                // Создаем копию массива категорий и перемещаем категорию
                const updatedCategories = [...categories];
                const [movedCategory] = updatedCategories.splice(currentIndex, 1);
                updatedCategories.splice(newIndex, 0, movedCategory);

                // Обновляем порядок (sort_order) всех категорий в соответствии с новым положением
                const updatePromises = updatedCategories.map((category, index) => {
                    return supabaseServiceRole
                        .from('categories')
                        .update({ sort_order: index })
                        .eq('id', category.id);
                });

                const results = await Promise.all(updatePromises);

                // Проверяем ошибки в каждом из запросов
                for (const result of results) {
                    if (result.error) throw result.error;
                }

                return { success: true };
            } catch (error) {
                console.error('Ошибка при изменении порядка категорий:', error);
                throw error;
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['categories'] });
        },
        onError: (error) => {
            console.error('Ошибка при изменении порядка категорий:', error);
            alert('Не удалось изменить порядок категорий.');
        }
    });

    // Мутация для удаления категории
    const deleteCategoryMutation = useMutation({
        mutationFn: async (id: string) => {
            try {
                console.log('Удаление категории (прямой доступ через компонент):', id);

                // Сначала проверяем, используется ли категория в оборудовании
                const { data: equipmentCheck, error: checkError } = await supabaseServiceRole
                    .from('equipment')
                    .select('id')
                    .eq('category_id', id)
                    .limit(1);

                if (checkError) {
                    console.error('Ошибка при проверке использования категории:', checkError);
                    throw checkError;
                }

                if (equipmentCheck && equipmentCheck.length > 0) {
                    throw new Error('Категория используется в оборудовании и не может быть удалена');
                }

                const { data, error } = await supabaseServiceRole
                    .from('categories')
                    .delete()
                    .eq('id', id)
                    .select();

                if (error) {
                    console.error('Ошибка при удалении категории:', error);
                    throw error;
                }

                return data;
            } catch (error) {
                console.error('Ошибка при удалении категории:', error);
                throw error;
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['categories'] });
        },
        onError: (error) => {
            console.error('Ошибка при удалении категории:', error);
            alert('Не удалось удалить категорию. Возможно, она используется в оборудовании.');
        }
    });

    // Обработчик изменения полей формы
    const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setCategoryForm((prev) => ({ ...prev, [name]: value }));
    };

    // Обработчик отправки формы
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!categoryForm.name.trim()) {
            setError('Название категории обязательно');
            return;
        }

        const currentDate = new Date().toISOString();
        createCategoryMutation.mutate({
            ...categoryForm,
            created_at: currentDate,
            updated_at: currentDate
        });
    };

    // Сброс формы
    const resetForm = () => {
        setCategoryForm({
            name: '',
            description: '',
            image_url: ''
        });
        setError(null);
    };

    // Обработчик удаления категории
    const handleDelete = (id: string) => {
        if (window.confirm('Вы уверены, что хотите удалить эту категорию? Если она используется в оборудовании, удаление будет невозможно.')) {
            deleteCategoryMutation.mutate(id);
        }
    };

    // Обработчик редактирования категории
    const handleEdit = (id: string) => {
        navigate(`/admin/categories/edit/${id}`);
    };

    // Обработчик перемещения категории вверх
    const handleMoveUp = (id: string) => {
        moveOrderMutation.mutate({ categoryId: id, direction: 'up' });
    };

    // Обработчик перемещения категории вниз
    const handleMoveDown = (id: string) => {
        moveOrderMutation.mutate({ categoryId: id, direction: 'down' });
    };

    return (
        <div className="max-w-6xl mx-auto px-4">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 md:mb-8 gap-4">
                <div>
                    <h1 className="text-2xl md:text-3xl font-semibold text-apple-800">Категории оборудования</h1>
                    <p className="text-apple-500 mt-2">
                        Управление категориями оборудования для аренды
                    </p>
                </div>
                <Button onClick={() => setIsModalOpen(true)} variant="primary" className="w-full md:w-auto">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    Добавить категорию
                </Button>
            </div>

            {/* Список категорий */}
            <div className="bg-white rounded-apple shadow-apple border border-apple-100 overflow-hidden">
                {isLoading ? (
                    <div className="p-8 text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                        <p className="mt-4 text-apple-500">Загрузка категорий...</p>
                    </div>
                ) : isError ? (
                    <div className="p-8 text-center text-red-600">
                        <p>Ошибка при загрузке категорий. Попробуйте перезагрузить страницу.</p>
                    </div>
                ) : categories && categories.length > 0 ? (
                    <div>
                        {/* Десктопная версия - таблица */}
                        <div className="hidden md:block">
                            <table className="min-w-full divide-y divide-apple-100">
                                <thead className="bg-apple-50">
                                    <tr>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-apple-500 uppercase tracking-wider">
                                            Название
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-apple-500 uppercase tracking-wider">
                                            Описание
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-apple-500 uppercase tracking-wider">
                                            Изображение
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-apple-500 uppercase tracking-wider">
                                            Дата создания
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-apple-500 uppercase tracking-wider">
                                            Порядок
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-apple-500 uppercase tracking-wider">
                                            Действия
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-apple-100">
                                    {categories.map((category: Category, index: number) => (
                                        <tr key={category.id} className="hover:bg-apple-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-apple-800">{category.name}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-apple-500">{category.description || '—'}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-apple-500">
                                                {category.image_url ? (
                                                    <div className="h-10 w-10 rounded-apple overflow-hidden">
                                                        <img src={category.image_url} alt={category.name} className="h-full w-full object-cover" />
                                                    </div>
                                                ) : (
                                                    '—'
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-apple-500">
                                                {new Date(category.created_at || '').toLocaleDateString()}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-apple-500">
                                                <div className="flex items-center space-x-2">
                                                    <span>{category.sort_order || index + 1}</span>
                                                    <div className="flex flex-col">
                                                        <button
                                                            className="text-gray-500 hover:text-primary p-1"
                                                            onClick={() => handleMoveUp(category.id)}
                                                            disabled={index === 0}
                                                            title="Переместить вверх"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                                            </svg>
                                                        </button>
                                                        <button
                                                            className="text-gray-500 hover:text-primary p-1"
                                                            onClick={() => handleMoveDown(category.id)}
                                                            disabled={index === (categories?.length || 0) - 1}
                                                            title="Переместить вниз"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <button className="text-primary hover:text-primary-dark mr-3" onClick={() => handleEdit(category.id)}>Редактировать</button>
                                                <button className="text-red-500 hover:text-red-700" onClick={() => handleDelete(category.id)}>Удалить</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Мобильная версия - карточки */}
                        <div className="md:hidden">
                            <div className="divide-y divide-apple-100">
                                {categories.map((category: Category, index: number) => (
                                    <div key={category.id} className="p-4 hover:bg-apple-50">
                                        <div className="flex justify-between mb-2">
                                            <div className="font-medium text-apple-800">{category.name}</div>
                                            <div className="flex items-center space-x-2">
                                                <span className="text-apple-500 text-sm">#{category.sort_order || index + 1}</span>
                                                <div className="flex space-x-1">
                                                    <button
                                                        className="text-gray-500 hover:text-primary p-2"
                                                        onClick={() => handleMoveUp(category.id)}
                                                        disabled={index === 0}
                                                        title="Переместить вверх"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                                        </svg>
                                                    </button>
                                                    <button
                                                        className="text-gray-500 hover:text-primary p-2"
                                                        onClick={() => handleMoveDown(category.id)}
                                                        disabled={index === (categories?.length || 0) - 1}
                                                        title="Переместить вниз"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        {category.description && (
                                            <div className="text-sm text-apple-500 mb-2">{category.description}</div>
                                        )}

                                        <div className="flex justify-between items-center mt-3">
                                            <div className="flex items-center">
                                                {category.image_url ? (
                                                    <div className="h-8 w-8 rounded-apple overflow-hidden mr-2">
                                                        <img src={category.image_url} alt={category.name} className="h-full w-full object-cover" />
                                                    </div>
                                                ) : null}
                                                <span className="text-xs text-apple-500">
                                                    {new Date(category.created_at || '').toLocaleDateString()}
                                                </span>
                                            </div>
                                            <div className="flex space-x-2">
                                                <button
                                                    className="bg-white border border-primary text-primary py-1 px-3 rounded text-sm hover:bg-primary-50"
                                                    onClick={() => handleEdit(category.id)}
                                                >
                                                    Ред.
                                                </button>
                                                <button
                                                    className="bg-white border border-red-500 text-red-500 py-1 px-3 rounded text-sm hover:bg-red-50"
                                                    onClick={() => handleDelete(category.id)}
                                                >
                                                    Удал.
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="p-8 text-center text-apple-500">
                        <p>Нет категорий. Создайте первую категорию оборудования.</p>
                    </div>
                )}
            </div>

            {/* Модальное окно для добавления категории */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-apple p-4 md:p-8 w-full max-w-lg shadow-2xl">
                        <div className="flex justify-between items-center mb-4 md:mb-6">
                            <h2 className="text-xl md:text-2xl font-semibold text-apple-800">Добавить категорию</h2>
                            <button
                                onClick={() => {
                                    setIsModalOpen(false);
                                    resetForm();
                                }}
                                className="text-apple-500 hover:text-apple-800 p-2"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {error && (
                            <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-apple border border-red-200">
                                {error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            <div className="mb-4">
                                <label htmlFor="name" className="block text-sm font-medium text-apple-700 mb-1">
                                    Название категории*
                                </label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={categoryForm.name}
                                    onChange={handleFormChange}
                                    className="w-full px-4 py-2 border border-apple-200 rounded-apple focus:ring-2 focus:ring-primary focus:border-transparent"
                                    placeholder="Введите название категории"
                                    required
                                />
                            </div>

                            <div className="mb-4">
                                <label htmlFor="description" className="block text-sm font-medium text-apple-700 mb-1">
                                    Описание
                                </label>
                                <textarea
                                    id="description"
                                    name="description"
                                    value={categoryForm.description || ''}
                                    onChange={handleFormChange}
                                    className="w-full px-4 py-2 border border-apple-200 rounded-apple focus:ring-2 focus:ring-primary focus:border-transparent"
                                    placeholder="Введите описание категории"
                                    rows={3}
                                />
                            </div>

                            <div className="mb-6">
                                <label htmlFor="image_url" className="block text-sm font-medium text-apple-700 mb-1">
                                    URL изображения
                                </label>
                                <input
                                    type="text"
                                    id="image_url"
                                    name="image_url"
                                    value={categoryForm.image_url || ''}
                                    onChange={handleFormChange}
                                    className="w-full px-4 py-2 border border-apple-200 rounded-apple focus:ring-2 focus:ring-primary focus:border-transparent"
                                    placeholder="https://example.com/image.jpg"
                                />
                                <p className="mt-1 text-sm text-apple-500">
                                    Укажите URL изображения для категории (необязательно)
                                </p>
                            </div>

                            <div className="flex flex-col md:flex-row md:justify-end gap-3">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    className="w-full md:w-auto"
                                    onClick={() => {
                                        setIsModalOpen(false);
                                        resetForm();
                                    }}
                                >
                                    Отмена
                                </Button>
                                <Button
                                    type="submit"
                                    variant="primary"
                                    className="w-full md:w-auto"
                                    disabled={createCategoryMutation.isPending}
                                >
                                    {createCategoryMutation.isPending ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                            Создание...
                                        </>
                                    ) : (
                                        'Создать категорию'
                                    )}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminCategoriesList; 